import * as THREE from 'three';
import { hexToWorld, NEV_MARK, NEV_MIRROR, NEV_PILE, nevBrazierAt, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** Cold: a snowflake. */
const COLD = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M2.4 4.75l11.2 6.5M2.4 11.25l11.2-6.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M6.2 2.6 8 4.2l1.8-1.6M6.2 13.4 8 11.8l1.8 1.6M2.6 7.1l2.3.7.5-2.3M13.4 8.9l-2.3-.7-.5 2.3M2.6 8.9l2.3-.7.5 2.3M13.4 7.1l-2.3.7-.5-2.3" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/** A brazier: a bowl on three legs, a flame in it. */
const BRAZIER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.5h11c0 2-2.4 3.4-5.5 3.4S2.5 10.5 2.5 8.5z" fill="currentColor"/><path d="M5 11.4 3.6 14.5M11 11.4l1.4 3.1M8 11.9v2.6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M8 1.5c1.8 2 2.6 3.4 1.6 5.2.1-1-.6-1.6-1.2-2-.1 1.3-1.6 1.8-1.6 3-.9-1.6-.3-3.5 1.2-6.2z" fill="currentColor"/></svg>';
/** A brazier gone out: the bowl, smoke over it. */
const EMBERS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.5h11c0 2-2.4 3.4-5.5 3.4S2.5 10.5 2.5 8.5z" fill="currentColor"/><path d="M5 11.4 3.6 14.5M11 11.4l1.4 3.1M8 11.9v2.6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M7 7c-1-1.2 1-2 0-3.4M9.4 7c-1-1.2 1-2 0-3.4" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" opacity=".7"/></svg>';
/** A mirror on its stand. */
const MIRROR = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="6.5" rx="4.6" ry="4.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M5.6 5.2l3-2.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M8 11.1V14.5M5.5 14.5h5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** The chapel's door. */
const DOOR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 14.5V6.5a4.5 4.5 0 0 1 9 0v8z" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4v10.5" stroke="currentColor" stroke-width="1"/><circle cx="9.4" cy="9.5" r=".7" fill="currentColor"/></svg>';
/** The ash watcher: wings spread. */
const WINGS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 6.2c-1.4-2.6-4.2-3.6-6.5-3 1.2.9 1.4 2 1.2 3 1-.3 2 0 2.7.8-.7.2-1.1.8-1.1 1.6 1-.6 2.4-.4 3.7.9 1.3-1.3 2.7-1.5 3.7-.9 0-.8-.4-1.4-1.1-1.6.7-.8 1.7-1.1 2.7-.8-.2-1 0-2.1 1.2-3-2.3-.6-5.1.4-6.5 3z" fill="currentColor"/><path d="M8 9.2v4.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** The family: a woman and two small ones. */
const FAMILY = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="5" cy="3.8" r="1.8" fill="currentColor"/><path d="M2 14.5c0-4 1.2-7 3-7s3 3 3 7z" fill="currentColor"/><circle cx="10.6" cy="7.2" r="1.3" fill="currentColor"/><path d="M8.6 14.5c0-2.8.8-4.8 2-4.8s2 2 2 4.8z" fill="currentColor"/><circle cx="13.6" cy="9.4" r="1.1" fill="currentColor"/><path d="M12.1 14.5c0-2 .6-3.4 1.5-3.4s1.5 1.4 1.5 3.4z" fill="currentColor"/></svg>';

type Mark = { el: HTMLButtonElement; at: number; point: THREE.Vector3; ico: HTMLElement; state: HTMLElement | null; name: HTMLElement };

const secs = (ticks: number): number => Math.max(0, Math.ceil(ticks / 12));

/**
 * The cleric's last road on screen, chapter 3 (docs/sendas/clerigo.md, «La nieve»), inside a path's own plate under the
 * stretcher's rows (game/camino-hud.ts) and over the board:
 *
 * - every fight: Tessa's cold, a blue bar that drops (red under 40 %, and its words say why: no brazier near, the
 *   stretcher still, freezing).
 * - the white col: the braziers lit of the four; a mark on each one not lit yet (a tap sends the hero to light it, a ring
 *   fills while it does).
 * - the frozen mirrors: the braziers at the pile and in the hands, the door (dark, how many mirrors the beam goes by,
 *   lit). Over the board, the pile (how many are left), each base (the mirrors it thaws, by their numbers: the plan can
 *   be worked out before the first brazier) and each mirror (its number, frozen or free).
 * - the night in the corral: the watcher (up in the storm and when it dives, its shadow on whom, down: strike it now,
 *   fallen), the family (all safe, who is frozen), the braziers lit of the three; a mark on each brazier that is out, and
 *   a tag on the watcher's shadow while it is warned.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class NieveHud {
  readonly panel: HTMLElement;
  private kind: 'col' | 'frost' | 'corral';
  private v = new THREE.Vector3();
  private coldRow = h('div.bh-row.nv-cold');
  private coldBar = h('i');
  private coldText = h('span.al-wave');
  private rowA = h('div.bh-row.al-waverow.nv-a');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow.nv-b');
  private textB = h('span.al-wave');
  private rowC = h('div.bh-row.al-waverow.nv-c');
  private textC = h('span.al-wave');
  private marks: Mark[] = [];
  private shadow: HTMLElement | null = null;
  private shadowAt = new THREE.Vector3();
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
    private names: Record<string, string>,
  ) {
    const r = s.rules?.senda?.litter;
    this.kind = r?.kind === 'frost' ? 'frost' : r?.kind === 'corral' ? 'corral' : 'col';
    this.coldRow.append(h('span.bh-ico', { html: COLD }), h('span.bh-label', {}, t('senda.camino.cold')), h('span.bar.nv-bar', {}, this.coldBar), this.coldText);
    const rows: HTMLElement[] = [this.coldRow];
    const S = r?.snow;
    if (this.kind === 'col' && S) {
      this.rowA.append(h('span.bh-ico', { html: BRAZIER }), h('span.bh-label', {}, t('senda.camino.braziers')), this.textA);
      rows.push(this.rowA);
      S.braziers.forEach((b, i) => this.addMark(NEV_MARK + i, b, BRAZIER, t('senda.camino.brazier'), t('senda.camino.lightHint'), true));
    } else if (this.kind === 'frost' && S?.frost) {
      const F = S.frost;
      this.rowA.append(h('span.bh-ico', { html: BRAZIER }), h('span.bh-label', {}, t('senda.camino.braziers')), this.textA);
      this.rowB.append(h('span.bh-ico', { html: DOOR }), h('span.bh-label', {}, t('senda.camino.door')), this.textB);
      rows.push(this.rowA, this.rowB);
      this.addMark(NEV_PILE, F.pile, BRAZIER, t('senda.camino.pile'), t('senda.camino.pileHint'), true);
      F.bases.forEach((b, i) => this.addMark(NEV_MARK + i, hexToWorld(b.q, b.r), EMBERS, '', t('senda.camino.baseHint'), true, t('senda.camino.thaws'), '', (F.thaws[i] ?? []).map((m) => m + 1)));
      F.mirrors.forEach((m, i) => this.addMark(NEV_MIRROR + i, hexToWorld(m.q, m.r), MIRROR, `${t('senda.camino.mirror')} ${i + 1}`, t('senda.camino.mirrorHint'), true, '', String(i + 1)));
    } else if (this.kind === 'corral' && S?.corral) {
      this.rowA.append(h('span.bh-ico', { html: WINGS }), h('span.bh-label', {}, t('senda.camino.watcher')), this.textA);
      this.rowB.append(h('span.bh-ico', { html: FAMILY }), h('span.bh-label', {}, t('senda.camino.family')), this.textB);
      this.rowC.append(h('span.bh-ico', { html: BRAZIER }), h('span.bh-label', {}, t('senda.camino.braziers')), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
      S.braziers.forEach((b, i) => this.addMark(NEV_MARK + i, b, EMBERS, t('senda.camino.brazierOut'), t('senda.camino.lightHint'), true));
      // The watcher's shadow: a tag on the floor where it will land (not a tap: the hero walks into it).
      this.shadow = h('span.al-stone.nv-shadow', { 'data-test': 'nieve-shadow' }, h('b', {}, t('senda.camino.shadow')));
      this.board.append(this.shadow);
    }
    this.panel = h(`div.bh.nv-panel.nv-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, p: { x: number; y: number }, ico: string, name: string, hint: string, withState: boolean, pre = '', num = '', chips: number[] = []): void {
    // (A mirror's number in its ring, not its icon: on a phone the names over five mirrors and six bases ran together.)
    const icon = num ? h('span.cm-ico.nv-num', {}, num) : h('span.cm-ico', { html: ico });
    const state = withState ? h('b.nv-state') : null;
    // (A base's mirrors as small rings with their numbers, like the mirrors' own: on a phone a bare «2 · 4» over a base
    // read as nothing; the rings say «these mirrors».)
    const nm = h(
      `b.mn-name.cm-name${chips.length ? '.nv-thaws' : ''}`,
      {},
      ...(pre ? [h('span.nv-pre', {}, `${pre} `)] : []),
      ...(chips.length ? chips.map((c) => h('i.nv-chip', {}, String(c))) : [name]),
    );
    const cls = at === NEV_PILE ? '.nv-pile' : at >= NEV_MIRROR ? '.nv-mirror' : '.nv-brazier';
    const el = h(
      `button.al-stone.cm-mark.nv-mark${cls}`,
      { type: 'button', 'data-test': `nieve-mark-${at}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      icon,
      nm,
      ...(state ? [state] : []),
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, at, point: simToWorld(p.x, p.y, new THREE.Vector3(), at >= NEV_MIRROR ? 2.4 : 1.6), ico: icon, state, name: nm });
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.litter;
    const w = s.senda?.litter;
    const n = w?.nev;
    const S = r?.snow;
    if (!r || !w || !n || !S) return;
    const set = (el: HTMLElement, text: string): void => {
      if (el.textContent !== text) el.textContent = text;
    };
    // Tessa's cold.
    const k = Math.max(0, Math.min(1, n.cold / Math.max(1, S.cold.full)));
    setStyle(this.coldBar, 'width', `${(k * 100).toFixed(1)}%`);
    const warm = n.lit.some((v, i) => {
      if (!v) return false;
      const p = nevBrazierAt(r, i);
      return !!p && Math.hypot(p.x - w.x, p.y - w.y) <= S.cold.warmR;
    });
    const ct = n.cold <= 0 ? t('senda.camino.freezing') : warm ? t('senda.camino.warming') : this.kind === 'col' && !w.moving && w.done < 0 ? t('senda.camino.coldStill') : t('senda.camino.cooling');
    set(this.coldText, ct);
    this.coldRow.classList.toggle('warn', k < 0.4);
    this.coldRow.classList.toggle('good', warm && k >= 0.4);
    const lit = n.lit.reduce((a, v) => a + v, 0);
    if (this.kind === 'col') {
      set(this.textA, t('senda.camino.litOf', { n: lit, of: n.lit.length }));
    } else if (this.kind === 'frost' && S.frost) {
      set(this.textA, n.carry ? t('senda.camino.inHand', { n: n.pile }) : t('senda.camino.atPile', { n: n.pile }));
      this.rowA.classList.toggle('good', !!n.carry);
      set(this.textB, w.lit >= 0 ? t('senda.camino.doorLit') : t('senda.camino.doorDark', { n: w.hits }));
      this.rowB.classList.toggle('good', w.lit >= 0);
    } else if (this.kind === 'corral' && S.corral) {
      const V = S.corral.watcher;
      const boss = s.enemies.find((e) => e.id === n.boss && e.hp > 0);
      const whom = n.who ? (this.names[n.who] ?? n.who) : t('senda.camino.you');
      const wt = !boss && n.boss ? t('senda.camino.watcherFell') : n.at >= 0 ? t('senda.camino.watcherDive', { name: whom, n: secs(n.at - s.waveT) }) : s.waveT < n.down ? t('senda.camino.watcherDown', { n: secs(n.down - s.waveT) }) : t('senda.camino.watcherUp', { n: secs(n.dive - V.warn - s.waveT) });
      set(this.textA, wt);
      this.rowA.classList.toggle('warn', n.at >= 0);
      this.rowA.classList.toggle('good', !!boss && s.waveT < n.down && n.at < 0);
      const fz = n.frozen.map((id) => this.names[id] ?? id);
      set(this.textB, fz.length ? t('senda.camino.frozen', { name: fz.join(', ') }) : t('senda.camino.familySafe'));
      this.rowB.classList.toggle('warn', fz.length > 0);
      set(this.textC, t('senda.camino.litOf', { n: lit, of: n.lit.length }));
      this.rowC.classList.toggle('warn', lit < n.lit.length);
    }
    // The marks.
    const key = `${n.lit.join('')}|${n.ice.join('')}|${n.pile}|${n.carry}|${w.lit}|${w.done}|${s.result}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        let gone: boolean;
        if (m.at === NEV_PILE) {
          gone = w.lit >= 0 || n.pile <= 0 || !!n.carry;
          if (m.state) m.state.textContent = t('senda.camino.pileLeft', { n: n.pile });
        } else if (m.at >= NEV_MIRROR) {
          const i = m.at - NEV_MIRROR;
          const iced = !!n.ice[i];
          gone = w.lit >= 0;
          m.el.classList.toggle('iced', iced);
          if (m.state) m.state.textContent = t(iced ? 'senda.camino.iced' : 'senda.camino.free');
        } else {
          const i = m.at - NEV_MARK;
          const on = !!n.lit[i];
          m.el.classList.toggle('has', on);
          if (this.kind === 'frost') {
            gone = w.lit >= 0;
            if (m.ico.dataset.k !== (on ? 'f' : 'e')) {
              m.ico.dataset.k = on ? 'f' : 'e';
              m.ico.innerHTML = on ? BRAZIER : EMBERS;
            }
            // (Lit, its ring says so, red and glowing: the word «lit» under it covered two mirrors' numbers on a phone.)
            if (m.state) m.state.textContent = on ? '' : n.carry ? t('senda.camino.baseSet') : '';
          } else {
            // (The col's and the corral's: only the ones out, to light again.)
            gone = on || (this.kind === 'col' && w.done >= 0);
            if (m.state) m.state.textContent = '';
          }
        }
        m.el.classList.toggle('gone', gone);
        m.el.disabled = s.result !== 'running';
        remeasureMark(m.el);
      }
    }
    const need = S.frost ? S.frost : null;
    for (const m of this.marks) {
      const sent = w.job === m.at;
      m.el.classList.toggle('sent', sent);
      const of = m.at >= NEV_MIRROR ? (need?.stand ?? 12) : m.at === NEV_PILE ? (need?.take ?? 12) : need && n.lit[m.at - NEV_MARK] ? need.take : S.cold.light;
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, of)).toFixed(2) : '0');
    }
    if (this.shadow) {
      const on = n.at >= 0;
      this.shadow.classList.toggle('on', on);
      if (on) simToWorld(n.ax, n.ay, this.shadowAt, 0.3);
    }
  }

  /** Every frame: the marks over their places, the shadow's tag over the shadow. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
    if (this.shadow) {
      if (!this.shadow.classList.contains('on')) {
        setStyle(this.shadow, 'display', 'none');
        return;
      }
      this.v.copy(this.shadowAt).project(camera);
      if (this.v.z > 1) {
        setStyle(this.shadow, 'display', 'none');
        return;
      }
      setStyle(this.shadow, 'display', '');
      pinMark(this.shadow, Math.min(w - 50, Math.max(50, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
